/**
 * The face registry.
 *
 * Adding a face is one file under `faces/` exporting `create`, plus one line in
 * {@link LOADERS}. Nothing else in the package needs to change, and the app that
 * mounts a face never learns which one it got.
 */

import type { Face, FaceOptions, FaceFactory } from './types.js';
import type { OrbOptions } from './faces/orb.js';
import type { VrmOptions } from './faces/vrm.js';
import type { MeshOptions } from './faces/mesh.js';
import type { LayeredOptions } from './faces/layered.js';

/** Loaded on demand, so a page that only shows the DOM face never pulls in three.js. */
const LOADERS = {
  orb: () => import('./faces/orb.js'),
  vrm: () => import('./faces/vrm.js'),
  dom: () => import('./faces/dom.js'),
  mesh: () => import('./faces/mesh.js'),
  layered: () => import('./faces/layered.js'),
} satisfies Record<string, () => Promise<{ create: FaceFactory<never> }>>;

export type FaceId = keyof typeof LOADERS;

/** Per-face option types, so `createFace('vrm', { modelUrl })` type-checks. */
export interface FaceOptionsMap {
  orb: OrbOptions;
  vrm: VrmOptions;
  dom: FaceOptions;
  mesh: MeshOptions;
  layered: LayeredOptions;
}

export const FACE_IDS = Object.keys(LOADERS) as FaceId[];

/** The face used when nothing is specified, and the fallback for faces that need assets. */
export const DEFAULT_FACE_ID: FaceId = 'orb';

export function isFaceId(id: string): id is FaceId {
  return Object.hasOwn(LOADERS, id);
}

export async function createFace<K extends FaceId>(
  id: K,
  opts?: FaceOptionsMap[K],
): Promise<Face> {
  if (!isFaceId(id)) throw new Error(`Unknown face '${id}'. Known: ${FACE_IDS.join(', ')}`);
  const module = (await LOADERS[id]()) as { create: FaceFactory<FaceOptionsMap[K]> };
  return module.create(opts);
}
